import { TFunction } from 'i18next';
import { showDeviceStatusDialog } from 'src/device-status/device-status';
import { IDeviceConfigurationDictionary } from 'src/device-status/interfaces/IDeviceConfigurationDictionary';
import { emeraldInitializePayment } from 'src/devices/payment/payment-controller';
import {
  beginPaymentSession,
  enableCardEntry,
  initializePaymentService,
  openLane,
  updatePinPadStatus,
} from 'src/devices/payment/pinpad-operations';
import { initializePeripheralConnection } from 'src/devices/rds-connection';
import { popupMessage } from '../../messages/business-logic/popup-message';
import { updateTouchPointConfiguration } from '../../touchpoint/business-logic/touchpoint-configuration';
import { EmeraldErrorModal } from '../api/emerald-error';
import { popEmeraldModal } from '../emerald-modal-manager/emerald-modal-manager';
import { DeviceType } from '../enumerations/device-type';
import { EmeraldDialogTypes } from '../enumerations/emerald-dialog-types';
import { delay } from '../helpers/delay-helper';
import { createSpan, endSpan } from '../helpers/tracing-helper';
import { IDeviceStatusItem } from '../interfaces/states/IDeviceStatusItem';
import { store } from '../redux/configure-store';
import { DEVICE_CHECK_INTERVAL, initializeDevices } from './initialize-devices';

export const reInitDevices = async (
  t: TFunction,
  device?: IDeviceStatusItem,
  showDeviceModal?: boolean,
  onFinish?: (arg: boolean) => void,
): Promise<void> => {
  if (device) {
    const reinitDevice:
      | IDeviceConfigurationDictionary
      | undefined = store
      .getState()
      .touchPoint.touchPointConfiguration.deviceList?.find(
        d => d.LogicalName === device.logicalName,
      );
    if (reinitDevice) {
      if (device.deviceType === DeviceType.CCL) {
        const span = createSpan('[RPS] initiate-payment', undefined, true, true);
        await emeraldInitializePayment(span.spanContext())
          .then(() => initializePaymentService(span.spanContext()))
          .then(() => endSpan(span))
          .then(() => restorePinpadState())
          .catch(() => {
            updatePinPadStatus(true, false);
            retryConnection(device, t);
          });
      } else {
        initializePeripheralConnection([reinitDevice])
          .then(() => checkDeviceStatus(t, device, showDeviceModal, onFinish))
          .catch(() => {
            retryConnection(device, t, onFinish);
          });
      }
    }
  } else {
    await updateTouchPointConfiguration()
      .then((): void => {
        initializeDevices();
      })
      .catch((err): void => {
        popupMessage(err);
      });
  }
};

const checkDeviceStatus = async (
  t: TFunction,
  device?: IDeviceStatusItem,
  showDeviceModal?: boolean,
  onFinish?: (arg: boolean) => void,
): Promise<void> => {
  await delay(DEVICE_CHECK_INTERVAL);
  const devices = store.getState().deviceStatus.devices;
  const deviceInitialized = devices.find(d => d.logicalName === device?.logicalName)?.initialized;
  if (!deviceInitialized) {
    const unInitializedDevices = devices.filter(d => d.initialized === false).length;
    retryConnectModal(t, device, unInitializedDevices);
  } else {
    if (showDeviceModal) {
      showDeviceStatusDialog(onFinish);
    }
  }
};

const restorePinpadState = async (): Promise<void> => {
  const loggedIn = store.getState().user.initialized;
  const isActiveTransaction = store.getState().transaction.transactionIdExists;
  if (!loggedIn) {
    return;
  } else {
    await openLane();
    if (isActiveTransaction) {
      await beginPaymentSession().then(() => enableCardEntry());
    }
  }
};

const retryConnection = (
  device: IDeviceStatusItem,
  t: TFunction,
  onFinish?: (arg: boolean) => void,
): void => {
  const unInitializedDevices = store
    .getState()
    .deviceStatus.devices.filter(d => d.initialized === false).length;
  retryConnectModal(t, device, unInitializedDevices, onFinish);
};

const retryConnectModal = (
  t: TFunction,
  device?: IDeviceStatusItem,
  unInitializedDevices?: number,
  onFinish?: (arg: boolean) => void,
): void => {
  popEmeraldModal('DeviceMenuModal');
  popupMessage(
    new EmeraldErrorModal({
      type: EmeraldDialogTypes.Error,
      message: 'Connection_Failed',
      subMessage: `${t('Connection_Failed_Sub_Message1')} ${device?.name} ${t(
        'Connection_Failed_Sub_Message2',
      )}`,
      acceptButtonLabel: 'DeviceRetry',
      closeButtonLabel: 'NotNow',
      onAccept: async (): Promise<void> => {
        await reInitDevices(t, device, true, onFinish);
      },
      onCancel:
        unInitializedDevices && unInitializedDevices > 1
          ? () => showDeviceStatusDialog(onFinish)
          : () => {},
    }),
  );
};
